import { graphql, fetchQuery } from 'relay-runtime';
import type { publicEventsBridgeMethod_getExtensionListQuery } from './__generated__/publicEventsBridgeMethod_getExtensionListQuery.graphql';
import { onPublic as subscribeToPublicEvent } from '../../custom-ui/bridge/methods/events';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { createAtlAttributionHeader } from '../../utils';
import type { ExtensionDetails, MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import { trackSubscription, type SubscriptionHandle } from './utils';

// ─── Context slices ───────────────────────────────────────────────────────────

type OnPublicContext = MakeBridgeContext<'extensionDetails' | 'registerCleanup'>;
type EmitPublicContext = MakeBridgeContext<'contextIds' | 'extensionDetails', 'relayEnvironment'>;

// ─── Payload / result shapes ──────────────────────────────────────────────────

type EventCallbackPayload = {
	callback: (payload?: any) => Promise<any>;
	event: string;
};

type EmitPublicPayload = {
	event: string;
	payload?: any;
};

// ─── Cache ────────────────────────────────────────────────────────────────────

/**
 * Module-level cache of allowed-recipients lists keyed on the sorted, joined
 * contextIds string. Sorting makes the key order-independent; joining with
 * a null byte avoids false collisions between adjacent entries.
 * The number of distinct keys in a session is bounded by the number of distinct
 * page contexts visited, so there is no meaningful memory growth.
 *
 * Exposed via `clearAllowedRecipientsCache` for test teardown.
 */
const allowedRecipientsCache = new Map<string, string[]>();

/** Clears the module-level recipients cache. Call in `afterEach` in tests. */
export const clearAllowedRecipientsCache = (): void => allowedRecipientsCache.clear();

// ─── GQL constants ────────────────────────────────────────────────────────────

const CORE_EVENT = 'core:event';
const CLIENT_EVENTS = 'client-events';
const CLIENT_EVENT = 'client-event';
const EVENT_PREFIX = 'forge.bridge.';

// ─── Shared helpers ───────────────────────────────────────────────────────────

/**
 * Mirrors `filterAllowedRecipients` in `useEmitPublicEvent.ts`.
 * Finds the `core:event` extension for this app with key `client-events` or
 * `client-event`, extracts the `allowedRecipients` property, strips `*`
 * wildcards, converts full ARIs to bare app IDs (last `/`-segment), and
 * unconditionally appends the sender's own `appId` so the emitting extension
 * always receives its own public events.
 */
const filterAllowedRecipients = (
	allExtensions: Array<{ appId?: string | null; key?: string | null; properties?: any }>,
	extension: ExtensionDetails,
): string[] => {
	const matchingExtension = allExtensions.find(
		(ext) =>
			ext.appId &&
			ext.key &&
			ext.appId === extension.appId &&
			(ext.key === CLIENT_EVENTS || ext.key === CLIENT_EVENT),
	);
	const allowedRecipients: string[] = matchingExtension?.properties?.allowedRecipients ?? [];

	const filtered: string[] = allowedRecipients
		.filter((r) => r !== '*')
		.map((r) => r.split('/').at(-1) ?? r);

	if (!filtered.includes(extension.appId)) {
		filtered.push(extension.appId);
	}
	return filtered;
};

/**
 * Fetches the allowed-recipients list for `emitPublic` by querying the
 * `core:event` extension manifest via Relay. Falls back to `[extensionDetails.appId]`
 * on network error so public events always reach the sending extension at minimum.
 */
const fetchAllowedRecipients = (
	relayEnvironment: EmitPublicContext['services']['relayEnvironment'],
	contextIds: string[],
	extensionDetails: ExtensionDetails,
): Promise<string[]> => {
	const observable = fetchQuery<publicEventsBridgeMethod_getExtensionListQuery>(
		relayEnvironment,
		graphql`
			query publicEventsBridgeMethod_getExtensionListQuery(
				$contextIds: [ID!]!
				$filter: [ExtensionContextsFilter!]!
			) {
				extensionContexts(contextIds: $contextIds) {
					extensions(filter: $filter) {
						appId
						properties
						key
					}
				}
			}
		`,
		{
			contextIds,
			filter: [
				{
					type: GQLExtensionContextsFilterType.ExtensionType,
					value: [CORE_EVENT],
				},
			],
		},
		{
			networkCacheConfig: {
				metadata: {
					headers: { 'atl-attribution': createAtlAttributionHeader() },
				},
			},
		},
	);

	return new Promise<string[]>((resolve) => {
		observable.subscribe({
			next: (result) => {
				const allExtensions = result?.extensionContexts?.[0]?.extensions ?? [];
				resolve(filterAllowedRecipients(Array.from(allExtensions), extensionDetails));
			},
			error: () => {
				resolve([extensionDetails.appId]);
			},
		});
	});
};

const getOrFetchAllowedRecipients = async (
	relayEnvironment: EmitPublicContext['services']['relayEnvironment'],
	contextIds: string[],
	extensionDetails: ExtensionDetails,
): Promise<string[]> => {
	const cacheKey = [...contextIds].sort().join('\0');
	if (!allowedRecipientsCache.has(cacheKey)) {
		const recipients = await fetchAllowedRecipients(relayEnvironment, contextIds, extensionDetails);
		allowedRecipientsCache.set(cacheKey, recipients);
		return recipients;
	}
	return allowedRecipientsCache.get(cacheKey)!;
};

// ─── Bridge methods ───────────────────────────────────────────────────────────

/**
 * Subscribes to a public forge.bridge DOM event. The callback fires only when
 * the dispatcher's `allowedRecipients` list includes this extension's `appId`
 * or the wildcard `*`.
 *
 * The subscription registers cleanup through `registerCleanup` so
 * `BridgeContextObject.destroy()` can batch-cleanup on unmount.
 */
export const onPublic: TBridgeMethod<
	OnPublicContext,
	EventCallbackPayload,
	SubscriptionHandle
> = async ({ extensionDetails, registerCleanup }, { event, callback }) => {
	const subscription = subscribeToPublicEvent(event, callback, extensionDetails);
	return trackSubscription(subscription, registerCleanup);
};

/**
 * Dispatches a public forge.bridge DOM event to a resolved set of recipient apps.
 *
 * The allowed-recipients list is fetched once via GQL on the first `emitPublic`
 * call and cached for the lifetime of the Forge UI instance. The sender's own
 * `appId` is always included even if absent from the manifest.
 */
export const emitPublic: TBridgeMethod<EmitPublicContext, EmitPublicPayload, void> = async (
	{ contextIds, extensionDetails, services: { relayEnvironment } },
	{ event, payload },
) => {
	const allowedRecipients = await getOrFetchAllowedRecipients(
		relayEnvironment,
		contextIds,
		extensionDetails,
	);

	const newEvent = new CustomEvent(`${EVENT_PREFIX}${event}`, {
		detail: {
			extension: extensionDetails,
			payload,
			allowedRecipients,
		},
	});

	window.document.dispatchEvent(newEvent);
};
